import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { kes, shiftLabel, isSunday } from "@/lib/format";

const isMonday = (d) => !!d && new Date(`${d}T12:00:00`).getDay() === 1;
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Wallet, AlertTriangle, ClipboardList, Loader2, Trash2, Scale } from "lucide-react";
import { isAccountant, isManager } from "@/lib/roles";

const monthLabel = (m) => (m ? new Date(`${m}-01T12:00:00`).toLocaleDateString("en-KE", { month: "long", year: "numeric" }) : "all months");

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="flex items-center gap-4 p-4">
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display text-xl font-bold text-slate-900">{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const site = useSite();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, shift_type: isSunday(today) ? "PM" : "AM", supervisor_name: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [view, setView] = useState("all");            // to_finish | submitted | all
  const [month, setMonth] = useState(today.slice(0, 7));

  const load = async () => {
    try {
      const { data } = await api.get("/shifts");
      setShifts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createShift = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/shifts", form);
      toast.success("Shift created");
      setOpen(false);
      navigate(`/shift/${data._id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const inMonth = shifts.filter((s) => !month || s.date.startsWith(month));
  const drafts = shifts.filter((s) => s.status === "draft");
  const needNote = drafts.filter((s) => s.computed_totals?.cash_status === "critical").length;
  const monthVariance = inMonth.reduce((a, s) => a + (s.computed_totals?.cash_variance || 0), 0);
  const monthSales = inMonth.reduce((a, s) => a + (s.computed_totals?.total_sales || 0), 0);
  const visible = inMonth.filter((s) => view === "all" || (view === "to_finish" ? s.status === "draft" : s.status !== "draft"));
  const canCreate = isManager(user) || isAccountant(user);
  const canDelete = isManager(user);

  const deleteShift = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/shifts/${deleteTarget._id}`);
      toast.success("Shift deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Shift Register</h1>
          <p className="mt-1 text-sm text-muted-foreground">{[site.name, site.operator, site.city.split(",")[0]].filter(Boolean).join(" · ")} (KES)</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-brand/90" data-testid="new-shift-button">
                <Plus className="mr-2 h-4 w-4" /> New Shift
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-shift-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Create Shift Record</DialogTitle>
              <DialogDescription>Choose the date and period. Duplicate date + period is blocked.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, shift_type: isSunday(e.target.value) ? "PM" : form.shift_type })} data-testid="shift-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Shift Period</Label>
                  <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v })}>
                    <SelectTrigger data-testid="shift-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isSunday(form.date) ? (
                        <SelectItem value="PM">Sun 1PM shift (Saturday 16:00 – Sunday 13:00)</SelectItem>
                      ) : (<>
                        <SelectItem value="AM">{isMonday(form.date) ? "8AM shift (Sunday 13:00 – 08:00)" : "8AM shift (16:00 the day before – 08:00)"}</SelectItem>
                        <SelectItem value="PM">4PM shift (08:00–16:00)</SelectItem>
                      </>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Supervisor Name</Label>
                  <Input value={form.supervisor_name} onChange={(e) => setForm({ ...form, supervisor_name: e.target.value })} placeholder="On-duty supervisor" data-testid="shift-supervisor-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createShift} disabled={creating} className="bg-brand hover:bg-brand/90" data-testid="create-shift-confirm">
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create &amp; Open
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Kpi icon={ClipboardList} label="Drafts to finish" value={drafts.length} accent="bg-blue-100 text-blue-700" />
        <Kpi icon={AlertTriangle} label="Need a note to submit" value={needNote} sub="cash variance over the limit" accent="bg-rose-100 text-rose-700" />
        <Kpi icon={Scale} label="Cash variance" value={`${monthVariance >= 0 ? "+" : ""}${kes(monthVariance, 0)}`} sub={monthLabel(month)}
          accent={monthVariance < 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"} />
        <Kpi icon={Wallet} label="Sales (KES)" value={kes(monthSales, 0)} sub={monthLabel(month)} accent="bg-emerald-100 text-emerald-700" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-lg border bg-white p-0.5" role="tablist" aria-label="Which shifts">
          {[["to_finish", "To finish"], ["submitted", "Submitted"], ["all", "All"]].map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)} data-testid={`shift-view-${v}`}
              className={`rounded-md px-3 py-1.5 text-sm ${view === v ? "bg-brand font-medium text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {l}{v === "to_finish" && drafts.length > 0 && <span className="ml-1.5 text-xs opacity-80">{drafts.length}</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Label htmlFor="shift-month" className="text-muted-foreground">Month</Label>
          <Input id="shift-month" type="month" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="shift-month" />
          {month && <Button variant="ghost" size="sm" onClick={() => setMonth("")}>All months</Button>}
        </div>
      </div>

      <Card className="overflow-hidden">
        <Table data-testid="shifts-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>To check</TableHead>
              <TableHead className="text-right">Total Sales</TableHead>
              <TableHead className="text-right">Cash Variance</TableHead>
              <TableHead>Submitted by</TableHead>
              {canDelete && <TableHead className="w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!loading && visible.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                {shifts.length === 0 ? "No shifts yet. Create your first shift." : view === "to_finish" ? "Nothing to finish - every shift here is submitted." : "No shifts for this month."}
              </TableCell></TableRow>
            )}
            {visible.map((s) => {
              const cv = s.computed_totals?.cash_variance || 0;
              const cs = s.computed_totals?.cash_status;
              return (
                <TableRow
                  key={s._id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/shift/${s._id}`)}
                  data-testid={`shift-row-${s._id}`}
                >
                  <TableCell className="font-medium">{s.date}</TableCell>
                  <TableCell>{shiftLabel(s.shift_type, s.date)}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell>
                    {(s.to_check || []).length === 0 ? (
                      <span className="text-xs text-emerald-700">All clear</span>
                    ) : (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cs === "critical" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}
                        title={s.to_check.join("\n")} data-testid={`to-check-${s._id}`}>
                        {s.to_check.length} to check
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono-fin">{kes(s.computed_totals?.total_sales)}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono-fin ${cs === "critical" ? "text-rose-600" : cs === "warning" ? "text-amber-600" : "text-emerald-700"}`}>
                      {cv >= 0 ? "+" : ""}{kes(cv)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.submitted_by_name || s.submitted_by || "—"}</TableCell>
                  {canDelete && (
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      {s.status === "draft" && (
                        <button
                          onClick={() => setDeleteTarget(s)}
                          className="text-slate-400 hover:text-rose-600"
                          data-testid={`delete-shift-${s._id}`}
                          title="Delete draft shift"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this draft shift?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>This permanently deletes the {deleteTarget.date} {shiftLabel(deleteTarget.shift_type, deleteTarget.date)} shift and all its entered data. This cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-shift-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteShift}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="delete-shift-confirm"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
